// @noflow
import { useReactiveVar } from '@apollo/client'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import dogsMeeting from 'assets/images/illustrations/dogs/dogs-meeting.svg'

import { Button } from '@/components/elements/atoms/Button'
import LoadingScreen from '@/components/elements/organisms/LoadingScreen/LoadingScreen'

import { PaymentMethodable, Provider } from '@/types'

import { State } from '../../PayOnOwnDevicePage'
import formatSubscriptionData from '../../helpers/buiildSubscriptionData'
import { validate } from '../../helpers/inputs'
import { useBuySubscriptionWithPaymentRequest } from '../../hooks/stripe/apple_and_google_pay/useBuySubscriptionWithPaymentRequest'
import buySubscriptionWithPaymentElements from '../../hooks/stripe/payment_elements/buySubscriptionWithPaymentElements'
import useBuySubscriptionWithBancontact from '../../hooks/stripe/sepa_direct_debit/useBuySubscriptionWithBancontact'
import useBuySubscriptionWithIdeal from '../../hooks/stripe/sepa_direct_debit/useBuySubscriptionWithIdeal'
import stripeMutationMethods from '../../hooks/stripe/sharedStripeMutationMethods'
import { submitSubscription } from '../../hooks/submitSubscription'
import {
  ActivePaymentViews,
  LoginUrlProps,
  paymentSubmissionState,
  setSubmissionState
} from '../../screens/PaymentScreen/PaymentScreen'
import { bannerMessageState } from '../Banner/Banner'
import { totalAmount } from '../DsAppOrderSummary/DsAppOrderSummary'

const stripePaymentMethodToPaymentMethod = (
  stripePaymentMethod: string
): ActivePaymentViews => {
  switch (stripePaymentMethod) {
    case 'card':
      return 'creditCard'
    case 'ideal':
      return 'iDeal'
    case 'bancontact':
      return 'bancontact'
    case 'google_pay':
      return 'googlePay'
    case 'apple_pay':
      return 'applePay'
    default:
      return 'creditCard'
  }
}

const PaymentElements = ({
  paymentPageState,
  setActivePaymentView,
  navigateToNextStep,
  namespace
}: {
  paymentPageState: State
  setActivePaymentView: (paymentType: ActivePaymentViews) => void
  navigateToNextStep: (input: LoginUrlProps) => void
  namespace: string
}): JSX.Element => {
  const stripe = useStripe()
  const elements = useElements()
  const { t } = useTranslation(namespace)
  const { amount } = useReactiveVar(totalAmount)
  const submissionState = useReactiveVar(paymentSubmissionState)
  const [paymentMethod, updatePaymentMethod] = useState<{
    type: string
    preselected: boolean
  }>({
    type: 'credit_card',
    preselected: true
  })

  const { bancontactAuthRecieved } = useBuySubscriptionWithBancontact({
    paymentPageState,
    setActivePaymentView,
    navigateToNextStep
  })

  const { idealAuthReceived } = useBuySubscriptionWithIdeal({
    paymentPageState,
    setActivePaymentView,
    navigateToNextStep
  })

  const { paymentRequestInProgress } = useBuySubscriptionWithPaymentRequest({
    paymentPageState,
    t,
    shouldSeeApplePay: true,
    shouldSeeGooglePay: true,
    submissionState,
    navigateToNextStep
  })

  const buySubscriptionCallback = useCallback(() => {
    if (!stripe || !elements) {
      bannerMessageState({
        message:
          'Unable to pay via stripe payment element due to stripe/elements unavailable'
      })
      return
    }
    buySubscriptionWithPaymentElements({
      stripe,
      elements,
      paymentPageState,
      amount,
      navigateToNextStep,
      paymentMethod: paymentMethod.type
    })
  }, [
    amount,
    elements,
    navigateToNextStep,
    paymentMethod,
    paymentPageState,
    stripe
  ])

  const handlePaymentElementChange = useCallback(
    (event: StripePaymentElementChangeEvent) => {
      if (!window.location.search.includes('redirect_status=succeeded')) {
        const paymentMethod = event.value.type
        updatePaymentMethod({
          type: paymentMethod,
          preselected: false
        })
        setActivePaymentView(stripePaymentMethodToPaymentMethod(paymentMethod))
      }
    },
    [setActivePaymentView]
  )

  const attemptPurchase = useCallback(
    (result, intentSecret) => {
      const paymentMethodId = result.data.sepaPaymentMethodIdFromIntentSecret
      const paymentIntentId = intentSecret.split('_secret_')[0]

      const formattedSubData = formatSubscriptionData({
        targetFirstDeliveryDate: paymentPageState.targetFirstDeliveryDate,
        subscriptionData: paymentPageState.data,
        provider: Provider.stripe,
        paymentMethodType: PaymentMethodable.SepaDirectDebit,
        paymentIntentId: paymentIntentId,
        supportedMarketingMethodPurposes: paymentPageState.marketingPreferences
      })
      return submitSubscription({
        data: formattedSubData,
        paymentMethodId: validate(
          paymentMethodId,
          'paymentMethodId',
          paymentPageState.data.directSalesPendingSubscription.address
            .shippingCountry.code
        ),
        navigateToNextStep
      })
    },
    [navigateToNextStep, paymentPageState]
  )

  const handleSubscriptionSubmission = useCallback(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const paymentIntentSecret = urlParams.get('payment_intent_client_secret')
      const intentSecret = paymentIntentSecret

      if (!intentSecret) {
        throw new Error('No intent secret found')
      }

      const { sepaPaymentMethodIdFromIntentSecret } = stripeMutationMethods()
      return sepaPaymentMethodIdFromIntentSecret({
        intentSecret,
        intentType: 'payment_intent'
      }).then((result) => {
        if (result) {
          attemptPurchase(result, intentSecret)
        }
      })
    } catch (error) {
      const { raiseBannerError } = stripeMutationMethods()
      raiseBannerError(error as Error)
    }
  }, [attemptPurchase])

  useEffect(() => {
    if (window.location.search.includes('redirect_status=succeeded')) {
      const handleAttemptPayment = async () => {
        await handleSubscriptionSubmission()
      }
      handleAttemptPayment()
    }
  }, [handleSubscriptionSubmission])

  useEffect(() => {
    if (
      idealAuthReceived ||
      bancontactAuthRecieved ||
      (paymentRequestInProgress.paymentMethod === 'googlePay' &&
        paymentRequestInProgress.inProgress)
    ) {
      setSubmissionState({ type: 'loading' })
    }
  }, [bancontactAuthRecieved, idealAuthReceived, paymentRequestInProgress])

  return (
    <>
      {submissionState.type === 'loading' ||
        (window.location.search.includes('redirect_status=succeeded') && (
          <div>
            <LoadingScreen
              isOpen
              title={{
                namespace: namespace,
                text: 'loading_screen.text',
                variant: 'display20'
              }}
              variant={'static'}
              image={dogsMeeting}
            />
          </div>
        ))}
      {!window.location.search.includes('redirect_status=succeeded') && (
        <>
          <PaymentElement
            options={{
              layout: 'tabs',
              terms: {
                card: 'never',
                ideal: 'never',
                bancontact: 'never',
                applePay: 'never',
                googlePay: 'never'
              },
              // We want to show iDeal and Bancontact first for NL and BE.
              // Any listed payment methods that aren't available in a
              // region will be ignored and the remaining payment methods
              // will be shown, e.g. GB users will see Card first as iDeal
              // and Bancontact are not available in GB
              paymentMethodOrder: ['ideal', 'bancontact']
            }}
            onChange={handlePaymentElementChange}
          />
          <div className="checkout__continue-button">
            <Button
              typography={{
                namespace,
                text:
                  submissionState.type === 'loading'
                    ? 'plan_steps.navigation.loading'
                    : 'plan_steps.navigation.pay'
              }}
              dataTestId="checkout-continue-to-pay-button"
              onClick={buySubscriptionCallback}
              disabled={submissionState.type === 'loading'}
              fullWidth
              disableAnalytics
            />
          </div>
        </>
      )}
    </>
  )
}

export default PaymentElements
